import { useState, lazy, Suspense } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  format,
  subDays,
  startOfWeek,
  endOfWeek,
  subWeeks,
  startOfMonth,
  endOfMonth,
  subMonths,
  startOfYear,
  endOfYear,
  subYears,
  startOfDay,
  endOfDay,
  eachDayOfInterval,
  addMonths,
  isSameDay,
  isBefore,
  isAfter,
  getDaysInMonth,
  startOfMonth as startOfMonthFn,
  getDay,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Trophy,
  Download,
  Search,
  Loader2,
  Percent,
  BarChart3,
  CalendarIcon,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@ouropix/ui';
import { Button } from '@ouropix/ui';
import { Popover, PopoverContent, PopoverTrigger } from '@ouropix/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ouropix/ui';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ouropix/ui';
import { api } from '@/lib/api';
import { ModeFilter, AdminMode } from '@/components/ui/ModeFilter';

const LazyRevenueChart = lazy(() =>
  import('@/components/financial/RevenueChart').then((m) => ({ default: m.RevenueChart }))
);

const WEEKDAYS = ['S', 'T', 'Q', 'Q', 'S', 'S', 'D'];

interface RangeCalendarProps {
  from: Date;
  to: Date;
  onChange: (range: { from: Date; to: Date }) => void;
  onClose: () => void;
  maxDate?: Date;
}

function RangeCalendar({ from, to, onChange, onClose, maxDate }: RangeCalendarProps) {
  const today = maxDate || new Date();
  const [viewMonth, setViewMonth] = useState(() => startOfMonthFn(from));
  const [firstPick, setFirstPick] = useState<Date | null>(null);
  const [hoverDate, setHoverDate] = useState<Date | null>(null);

  const daysInMonth = getDaysInMonth(viewMonth);
  const firstWeekday = getDay(viewMonth);

  const cells: (Date | null)[] = [
    ...Array(firstWeekday).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => new Date(viewMonth.getFullYear(), viewMonth.getMonth(), i + 1)),
  ];

  const rangeFrom = firstPick ?? from;
  const rangeTo = firstPick ? (hoverDate ?? firstPick) : to;
  const [rangeStart, rangeEnd] = isBefore(rangeFrom, rangeTo)
    ? [rangeFrom, rangeTo]
    : [rangeTo, rangeFrom];

  const handleDayClick = (date: Date) => {
    if (isAfter(date, today)) return;
    if (!firstPick) {
      setFirstPick(date);
    } else {
      const [s, e] = isBefore(firstPick, date)
        ? [firstPick, date]
        : [date, firstPick];
      onChange({ from: s, to: e });
      setFirstPick(null);
      setHoverDate(null);
      onClose();
    }
  };

  return (
    <div className="p-3 w-72">
      <div className="flex items-center justify-between mb-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={() => setViewMonth((m) => addMonths(m, -1))}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm font-medium capitalize">
          {format(viewMonth, 'MMMM yyyy', { locale: ptBR })}
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={() => setViewMonth((m) => addMonths(m, 1))}
          disabled={isAfter(addMonths(viewMonth, 1), today)}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {WEEKDAYS.map((d, i) => (
          <div key={i} className="text-center text-xs text-muted-foreground py-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((date, i) => {
          if (!date) return <div key={`e-${i}`} />;
          const isDisabled = isAfter(date, today);
          const isStart = isSameDay(date, rangeStart);
          const isEnd = isSameDay(date, rangeEnd);
          const inRange = !isBefore(date, rangeStart) && !isAfter(date, rangeEnd);
          const isSelected = isStart || isEnd;

          return (
            <button
              key={date.toISOString()}
              disabled={isDisabled}
              onClick={() => handleDayClick(date)}
              onMouseEnter={() => firstPick && setHoverDate(date)}
              className={[
                'h-8 text-xs rounded transition-colors',
                isDisabled ? 'text-muted-foreground/30 cursor-not-allowed' : 'hover:bg-accent cursor-pointer',
                isSelected ? 'bg-primary text-primary-foreground hover:bg-primary' : '',
                inRange && !isSelected ? 'bg-primary/15' : '',
              ].join(' ')}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
      {firstPick && (
        <p className="text-xs text-muted-foreground text-center mt-2">
          Selecione a data final
        </p>
      )}
    </div>
  );
}

export const Route = createFileRoute('/_admin/faturamento')({
  component: FaturamentoPage,
});

interface RevenueKpis {
  totalRevenue: number;
  totalPrizes: number;
  netReturn: number;
  margin: number;
}

interface RevenueDailyPoint {
  date: string;
  revenue: number;
  prizes: number;
}

interface RevenueGameRow {
  id: string;
  title: string;
  modality: string;
  revenue: number;
  prizes: number;
  netReturn: number;
  margin: number;
}

interface RevenueReport {
  kpis: RevenueKpis;
  dailyChart: RevenueDailyPoint[];
  gameTable: RevenueGameRow[];
}

interface Modality {
  code: string;
  name: string;
}

type DateRange = { from: Date; to: Date };

function formatCurrency(value: number): string {
  // Backend amounts ja em reais (Decimal). Nao dividir por 100.
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function FaturamentoPage() {
  const today = new Date();

  const [dateRange, setDateRange] = useState<DateRange>({
    from: subDays(today, 27),
    to: today,
  });
  const [modality, setModality] = useState('all');
  const [mode, setMode] = useState<AdminMode>('all');
  const [appliedMode, setAppliedMode] = useState<AdminMode>('all');
  const [appliedRange, setAppliedRange] = useState<DateRange>({
    from: subDays(today, 27),
    to: today,
  });
  const [appliedModality, setAppliedModality] = useState('all');
  const [activeQuickFilter, setActiveQuickFilter] = useState<string | null>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const quickPresets = [
    { label: 'Hoje', getRange: () => ({ from: today, to: today }) },
    { label: 'Ontem', getRange: () => ({ from: subDays(today, 1), to: subDays(today, 1) }) },
    {
      label: 'Esta semana',
      getRange: () => ({ from: startOfWeek(today, { weekStartsOn: 1 }), to: today }),
    },
    {
      label: 'Semana passada',
      getRange: () => {
        const s = startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 });
        return { from: s, to: endOfWeek(s, { weekStartsOn: 1 }) };
      },
    },
    { label: 'Este mês', getRange: () => ({ from: startOfMonth(today), to: today }) },
    {
      label: 'Mês passado',
      getRange: () => ({
        from: startOfMonth(subMonths(today, 1)),
        to: endOfMonth(subMonths(today, 1)),
      }),
    },
    { label: 'Este ano', getRange: () => ({ from: startOfYear(today), to: today }) },
    {
      label: 'Ano passado',
      getRange: () => ({
        from: startOfYear(subYears(today, 1)),
        to: endOfYear(subYears(today, 1)),
      }),
    },
  ];

  const handleQuickFilter = (label: string) => {
    const preset = quickPresets.find((p) => p.label === label);
    if (!preset) return;
    setDateRange(preset.getRange());
    setActiveQuickFilter(label);
  };

  const { data: modalities } = useQuery({
    queryKey: ['admin-modalities-revenue'],
    queryFn: () =>
      api
        .get<{ modalities: Modality[] }>('/admin/modalities')
        .then((r) => r.data.modalities ?? []),
  });

  const { data: report, isPending: reportLoading } = useQuery({
    queryKey: ['admin-revenue-report', appliedRange.from.toISOString(), appliedRange.to.toISOString(), appliedModality, appliedMode],
    queryFn: () =>
      api
        .get<RevenueReport>('/admin/financial/revenue', {
          params: {
            date_from: startOfDay(appliedRange.from).toISOString(),
            date_to: endOfDay(appliedRange.to).toISOString(),
            modality_id: appliedModality !== 'all' ? appliedModality : undefined,
            mode: appliedMode !== 'all' ? appliedMode : undefined,
          },
        })
        .then((r) => r.data),
  });

  const isLoading = reportLoading;

  const kpis = report?.kpis ?? { totalRevenue: 0, totalPrizes: 0, netReturn: 0, margin: 0 };
  const gameTable = report?.gameTable ?? [];

  const dailyChart = (() => {
    const dayMap = new Map((report?.dailyChart ?? []).map((p) => [p.date, p]));
    const days = eachDayOfInterval({ start: appliedRange.from, end: appliedRange.to });
    return days.map((d) => {
      const key = format(d, 'yyyy-MM-dd');
      const entry = dayMap.get(key);
      return {
        date: format(d, 'dd/MM', { locale: ptBR }),
        fullDate: format(d, "dd 'de' MMM", { locale: ptBR }),
        Receita: Number((entry?.revenue ?? 0).toFixed(2)),
        Prêmios: Number((entry?.prizes ?? 0).toFixed(2)),
      };
    });
  })();

  const handleSearch = () => {
    setAppliedRange({ ...dateRange });
    setAppliedModality(modality);
    setAppliedMode(mode);
  };

  const handleExportCSV = async () => {
    const res = await api.get<Blob>('/admin/financial/revenue/export', {
      params: {
        date_from: startOfDay(appliedRange.from).toISOString(),
        date_to: endOfDay(appliedRange.to).toISOString(),
        modality_id: appliedModality !== 'all' ? appliedModality : undefined,
        mode: appliedMode !== 'all' ? appliedMode : undefined,
      },
      responseType: 'blob',
    });
    const contentDisposition = res.headers['content-disposition'] ?? '';
    const filenameMatch = /filename="?([^"]+)"?/.exec(contentDisposition);
    const filename = filenameMatch
      ? filenameMatch[1]
      : `faturamento_${format(appliedRange.from, 'yyyyMMdd')}_${format(appliedRange.to, 'yyyyMMdd')}.csv`;
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatDateRangeLabel = () => {
    return `${format(dateRange.from, 'dd/MM/yyyy', { locale: ptBR })} - ${format(dateRange.to, 'dd/MM/yyyy', { locale: ptBR })}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <BarChart3 className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-3xl font-bold">Faturamento</h1>
          <p className="text-muted-foreground">Análise financeira de receitas, prêmios e retorno</p>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex flex-wrap gap-2 items-center">
            {quickPresets.map((preset) => (
              <Button
                key={preset.label}
                variant={activeQuickFilter === preset.label ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleQuickFilter(preset.label)}
              >
                {preset.label}
              </Button>
            ))}
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5">
                  <CalendarIcon className="h-4 w-4" />
                  {formatDateRangeLabel()}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="p-0 w-auto">
                <RangeCalendar
                  from={dateRange.from}
                  to={dateRange.to}
                  maxDate={today}
                  onChange={(range) => {
                    setDateRange(range);
                    setActiveQuickFilter(null);
                  }}
                  onClose={() => setCalendarOpen(false)}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex flex-col lg:flex-row gap-4 items-end">
            <div className="w-full lg:w-56">
              <label className="text-xs text-muted-foreground mb-1.5 block">Modalidade</label>
              <Select value={modality} onValueChange={setModality}>
                <SelectTrigger>
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {modalities?.map((m) => (
                    <SelectItem key={m.code} value={m.code}>
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1.5 block">Modo</label>
              <ModeFilter value={mode} onChange={setMode} />
            </div>
            <Button onClick={handleSearch} className="gap-2">
              <Search className="h-4 w-4" />
              Buscar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-emerald-500/10">
                <DollarSign className="h-5 w-5 text-emerald-500" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Receita Bruta</p>
                <p className="text-xl font-bold truncate">
                  {isLoading ? '—' : formatCurrency(kpis.totalRevenue)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-amber-500/10">
                <Trophy className="h-5 w-5 text-amber-500" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Prêmios Pagos</p>
                <p className="text-xl font-bold truncate">
                  {isLoading ? '—' : formatCurrency(kpis.totalPrizes)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-lg ${kpis.netReturn >= 0 ? 'bg-emerald-500/10' : 'bg-destructive/10'}`}>
                {kpis.netReturn >= 0 ? (
                  <TrendingUp className="h-5 w-5 text-emerald-500" />
                ) : (
                  <TrendingDown className="h-5 w-5 text-destructive" />
                )}
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Retorno Liquido</p>
                <p className={`text-xl font-bold truncate ${kpis.netReturn >= 0 ? 'text-emerald-500' : 'text-destructive'}`}>
                  {isLoading ? '—' : formatCurrency(kpis.netReturn)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-lg ${kpis.margin >= 0 ? 'bg-blue-500/10' : 'bg-destructive/10'}`}>
                <Percent className="h-5 w-5 text-blue-500" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Margem</p>
                <p className={`text-xl font-bold truncate ${kpis.margin >= 0 ? 'text-blue-500' : 'text-destructive'}`}>
                  {isLoading ? '—' : `${kpis.margin.toFixed(1)}%`}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Receita vs Prêmios por Dia</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-72">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Suspense
              fallback={
                <div className="flex justify-center p-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              }
            >
              <LazyRevenueChart data={dailyChart} />
            </Suspense>
          )}
        </CardContent>
      </Card>

      {/* Tabela por jogo */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-medium">Detalhamento por Jogo</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCSV}
            className="gap-2"
            disabled={gameTable.length === 0 || isLoading}
          >
            <Download className="h-4 w-4" />
            Exportar CSV
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : gameTable.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Nenhum dado encontrado para o período selecionado.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Jogo</TableHead>
                    <TableHead>Modalidade</TableHead>
                    <TableHead className="text-right">Receita</TableHead>
                    <TableHead className="text-right">Prêmios</TableHead>
                    <TableHead className="text-right">Retorno</TableHead>
                    <TableHead className="text-right">Margem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {gameTable.map((g) => (
                    <TableRow key={g.id}>
                      <TableCell className="font-medium max-w-48 truncate">{g.title}</TableCell>
                      <TableCell className="text-muted-foreground">{g.modality}</TableCell>
                      <TableCell className="text-right">{formatCurrency(g.revenue)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(g.prizes)}</TableCell>
                      <TableCell
                        className={`text-right font-medium ${g.netReturn >= 0 ? 'text-emerald-500' : 'text-destructive'}`}
                      >
                        {formatCurrency(g.netReturn)}
                      </TableCell>
                      <TableCell
                        className={`text-right font-medium ${g.margin >= 0 ? 'text-emerald-500' : 'text-destructive'}`}
                      >
                        {g.margin.toFixed(1)}%
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="border-t-2 font-bold">
                    <TableCell colSpan={2}>Total</TableCell>
                    <TableCell className="text-right">{formatCurrency(kpis.totalRevenue)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(kpis.totalPrizes)}</TableCell>
                    <TableCell
                      className={`text-right ${kpis.netReturn >= 0 ? 'text-emerald-500' : 'text-destructive'}`}
                    >
                      {formatCurrency(kpis.netReturn)}
                    </TableCell>
                    <TableCell
                      className={`text-right ${kpis.margin >= 0 ? 'text-emerald-500' : 'text-destructive'}`}
                    >
                      {kpis.margin.toFixed(1)}%
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
